import SearchIcon from '@mui/icons-material/Search';
import { Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { useQuery } from '@tanstack/react-query';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import queryString from 'query-string';
import React, { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { API_ADVERTISERS_CREATIVES_LIST_URL } from '../../constants/apiUrls';
import { ADVERTISERS_CREATIVES_NEW_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import useDebounce from '../../hooks/useDebounce';
import { camelCaseKeys } from '../../util/object';
import { getRequestJSON } from '../../util/services';
import CreativesDataGrid from './CreativesDataGrid';
import { AssociationType } from './CreativesForm';

interface CreativesListProps {
  advertiserId: number;
  adGroupId?: number;
  lineItemId?: number;
  productId?: number;
  associationType?: AssociationType;
}

function CreativesList(props: CreativesListProps) {
  // The advertiser-nested creatives index opts into the per-domain flip:
  // apiHostFor('creatives') resolves to ACH iff creatives is flipped ON (migrated
  // list + env gate), else the plain Rails apiHost. The {collection,pagination}
  // envelope is shape-identical for what this grid reads (collection + the
  // pagination.total_items count — RD-3 only changes the page/per_page meta keys
  // this consumer never reads), so only the host switches: no response adaptation.
  // The CSV export below stays on Rails apiHost (an un-migrated helper endpoint).
  const { apiHost, apiHostFor } = useConfig();
  const creativesHost = apiHostFor('creatives');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const { page, pageSize: perPage } = paginationModel;
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const { advertiserId, adGroupId, lineItemId, associationType = AssociationType.AdGroup, productId } = props;

  const contractConfigurationContext = useContractConfigurationContext();

  const contractId: number = get(contractConfigurationContext, 'contractData.id');

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const query = useQuery({
    queryKey: ['creatives', adGroupId, advertiserId, page, perPage, debouncedSearchTerm],
    queryFn: () =>
      getRequestJSON(
        creativesHost,
        `${generatePath(API_ADVERTISERS_CREATIVES_LIST_URL, {
          advertiserId: advertiserId.toString(),
          '*': `?${queryString.stringify({
            // Server page is 1-index and front-end is 0-index based.
            page: `${page + 1}`,
            per_page: perPage.toString(),
            term: debouncedSearchTerm,
            campaign_id: adGroupId ? adGroupId || '' : undefined,
          })}`,
        })}`
      ),
    select: camelCaseKeys,
  });

  const { data: creativesList, isLoading, refetch } = query;

  return (
    <Stack spacing={2}>
      <Typography variant="h5">Creatives ({creativesList?.collection?.length || 0})</Typography>
      <Stack>
        <Paper variant="outlined">
          <Stack p={2} spacing={2}>
            <Stack direction="row" spacing={2} justifyContent="end">
              <Button
                onClick={() =>
                  window.open(`${apiHost}/admin/advertisers/${advertiserId}/creative_metadata.csv`, '_blank')
                }
                disabled={isEmpty(creativesList)}
              >
                Download Creative IDs
              </Button>
              <Button
                onClick={() =>
                  navigate(
                    generatePath(ADVERTISERS_CREATIVES_NEW_URL, {
                      advertiserId: advertiserId.toString(),
                      '*': `?${queryString.stringify(
                        {
                          adGroupId: adGroupId?.toString() || '',
                          productId: productId?.toString() || '',
                          contractId: contractId?.toString() || '',
                        },
                        {}
                      )}`,
                    })
                  )
                }
              >
                New Creative
              </Button>
            </Stack>
            <Stack>
              <TextField
                sx={{ backgroundColor: 'white', width: '100%' }}
                variant="outlined"
                placeholder="Search by Creative Name"
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPaginationModel({ page: 0, pageSize: perPage });
                }}
                value={searchTerm}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                  endAdornment: (
                    <InputAdornment position="end">
                      {isLoading ? <CircularProgress color="inherit" size={24} /> : null}
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <CreativesDataGrid
              creativesList={creativesList}
              isLoading={isLoading}
              refetch={refetch}
              contractId={contractId}
              lineItemId={lineItemId}
              adGroupId={adGroupId}
              associationType={associationType}
              productId={productId}
              pagination
              paginationMode="server"
              pageSizeOptions={[5, 10, 25, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
}

export default CreativesList;
